import {
  AloftPrice,
  QualityLabel,
  RegressionOutputSuccess,
  SensitivityAnalysisResult,
  UnitRateModelFeatureKey,
  getRegressionRSquaredDescription,
  getRegressionSampleSizeDescription,
  getRegressionStandardErrorRatioDescription,
  getSensitivitySpreadRatioDescription,
  orUndefined,
} from '@aloft/toolkit-shared';
import { Flex, ProductColor, Tooltip, Typography, colors } from '@aloft/toolkit-sui';

const qualityColor = (label: QualityLabel): ProductColor => {
  if (label === 'EXCELLENT' || label === 'GOOD') return 'textSuccess';
  if (label === 'ACCEPTABLE') return 'textSecondary';
  if (label === 'WEAK') return 'textWarning';
  return 'textError';
};
type StatTileProps = {
  label: string;
  value: string;
  info: string | null;
  quality?: QualityLabel;
};

export const StatTile = ({ label, value, quality, info }: StatTileProps) =>
  info ? (
    <Tooltip label={info} placement="top" hasArrow>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="0.5rem"
        padding="1rem"
        backgroundColor="surface1"
        border={`1px solid ${colors.strokeDefault}`}
        borderRadius="8px"
        minWidth="140px"
        height="100px"
        flexShrink={0}
        __dangerDoNotUseSx={{ cursor: 'help' }}
      >
        <Typography variant="body2" color="textLight" textAlign="center">
          {label}
        </Typography>
        <Typography variant="h2">{value}</Typography>
        {quality && (
          <Typography variant="subtitle2" color={qualityColor(quality)}>
            {quality}
          </Typography>
        )}
      </Flex>
    </Tooltip>
  ) : (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="0.5rem"
      padding="1rem"
      backgroundColor="surface1"
      border={`1px solid ${colors.strokeDefault}`}
      borderRadius="8px"
      minWidth="140px"
      height="100px"
      flexShrink={0}
    >
      <Typography variant="body2" color="textLight" textAlign="center">
        {label}
      </Typography>
      <Typography variant="h2">{value}</Typography>
      {quality && (
        <Typography variant="subtitle2" color={qualityColor(quality)}>
          {quality}
        </Typography>
      )}
    </Flex>
  );

type StatsRowProps = {
  type: 'regression' | 'sensitivity';
  output?: RegressionOutputSuccess;
  result?: SensitivityAnalysisResult;
  featureKey?: UnitRateModelFeatureKey;
};

const getRegressionTiles = (output: RegressionOutputSuccess): StatTileProps[] => {
  const sampleSizeDescription = getRegressionSampleSizeDescription(output.sampleSize);
  const regressionRSquaredDescription = getRegressionRSquaredDescription(
    output.modelDiagnostics.rSquared,
  );
  const regressionStandardErrorRatioDescription = getRegressionStandardErrorRatioDescription(
    output.modelDiagnostics.standardErrorRatio,
  );
  return [
    {
      label: 'Sample Size',
      value: String(output.sampleSize),
      quality: sampleSizeDescription.label,
      info: sampleSizeDescription.warning,
    },
    { label: 'Variables', value: String(output.numberOfVariables), info: null },
    {
      label: 'Constant',
      value: AloftPrice.schema.parse(output.constant).toString(true),
      info: null,
    },
    {
      label: 'R-squared',
      value: `${(output.modelDiagnostics.rSquared * 100).toFixed(0)}%`,
      quality: regressionRSquaredDescription.label,
      info: regressionRSquaredDescription.warning,
    },
    {
      label: 'Standard Error',
      value: `${(output.modelDiagnostics.standardErrorRatio * 100).toFixed(0)}%`,
      quality: regressionStandardErrorRatioDescription.label,
      info: regressionStandardErrorRatioDescription.warning,
    },
  ];
};

const getSensitivityTiles = (
  result: SensitivityAnalysisResult,
  featureKey: UnitRateModelFeatureKey,
): StatTileProps[] => {
  const optimalRate =
    orUndefined(AloftPrice.schema.parse)(result.selectedRate.rate)?.toString(true) ?? '-';

  const { spreadRatio } = result.selectedRate;
  const spreadRatioDescription = getSensitivitySpreadRatioDescription(spreadRatio, featureKey);
  return [
    { label: 'Optimal Unit Rate', value: optimalRate, info: null },
    {
      label: 'Minimum Percent Variance',
      value: `${(spreadRatio * 100).toFixed(0)}%`,
      quality: spreadRatioDescription.label,
      info: spreadRatioDescription.warning,
    },
  ];
};

export const StatsRow = ({ type, output, result, featureKey }: StatsRowProps) => {
  const tiles =
    type === 'regression'
      ? getRegressionTiles(output!)
      : getSensitivityTiles(result!, featureKey ?? 'gla');

  return (
    <Flex gap="1.5rem" justifyContent="center" flexWrap="wrap">
      {tiles.map((tile) => (
        <StatTile key={tile.label} {...tile} />
      ))}
    </Flex>
  );
};
